import { ObservableMap } from '@stencil/store';
import jwt_decode from 'jwt-decode';

import { refreshAccountCache } from '../../utils/api/tenantsAndOrganizationInfoApi';
import { Features } from '../../utils/featureFlags/featureFlagConstants';
import { getFeatureFlag } from '../../utils/featureFlags/featureFlags';
import { setPortalShellUserLanguage } from '../../utils/languageHelper';
import Logger from '../../utils/logger/logger';
import { getAccessToken } from '../../utils/sessionHelper';
import { currentGravatarStorageKey } from '../../utils/storageKeys';
import { isFalselyOrWhiteSpace } from '../common';
import { State } from '../types/state';

export function setAccessTokenSideEffect(store: ObservableMap<State>) {
    store.onChange('accessToken', async accessToken => {
        if (
            !getFeatureFlag(Features.EnablePortalShellSelfAuth.name) ||
      !store.get('selfAuthAccessToken')
        ) {
            await handleAccessTokenChange(store, accessToken);
        }
    });
    store.onChange('selfAuthAccessToken', async accessToken => {
        await handleAccessTokenChange(store, accessToken, { isSelfAuth: true });
    });
}

async function handleAccessTokenChange(
    store: ObservableMap<State>,
    accessToken: string,
    { isSelfAuth = false } = {},
) {
    if (isFalselyOrWhiteSpace(accessToken)) {
        return;
    }

    const success = decodeAccessToken(store, accessToken);
    if (!success) {
        return;
    }

    const tokenType = isSelfAuth ? 'session self auth' : 'passed-in';
    Logger.debug([ 'api', 'token' ], `using ${tokenType} access token`, { state: store.state });

    const effectiveToken = getAccessToken(store, accessToken);
    Logger.debug(
        [ 'account', 'api', 'language', 'featureFlags' ],
        'api call, user language, and feature flags calls',
    );
    await Promise.all([
        refreshAccountCache(store, effectiveToken),
        setPortalShellUserLanguage(store, effectiveToken),
        window.PortalShell.FeatureFlags.fetchFeatureFlags(store, effectiveToken),
    ]);
}

function decodeAccessToken(store: ObservableMap<State>, accessToken: string) {
    let decodedToken: any = null;
    try {
        decodedToken = jwt_decode(accessToken);
    } catch (e) {
        Logger.error(
            [ 'store', 'token' ],
            'access token was invalid, Details : ' + e.toString(),
        );
    }

    if (decodedToken == null) {
        return false;
    }

    if (!decodedToken.scope) {
        Logger.warn(
            'token',
            `Warning: The ID Token should not be passed. Please pass an access token to the token prop.`,
            { forcePrintToConsole: true },
        );
    }

    const hasName = decodedToken.first_name || decodedToken.last_name;
    const fullName = hasName
        ? `${decodedToken.first_name} ${decodedToken.last_name}`
        : decodedToken.name;

    store.set('fullName', fullName);
    store.set('email', decodedToken.email);
    store.set('avatarUrl', decodedToken.picture);
    store.set('organizationId', decodedToken.prt_id);
    store.set('isHost', decodedToken.host?.toLowerCase() === 'true');
    store.set('userId', decodedToken.sub);
    store.set('connectionType', decodedToken.ext_idp_disp_name?.toLowerCase());

    Logger.info(
        [ 'session', 'token' ],
        `token info: nbf=${decodedToken.nbf}, exp=${decodedToken.exp}, client_id=${
            decodedToken.client_id
        }, scope=${JSON.stringify(decodedToken.scope)}`,
    );

    if (decodedToken.picture) {
        localStorage.setItem(currentGravatarStorageKey, decodedToken.picture);
    }

    return true;
}
